import React, { useEffect, useState, useRef } from "react";
import { Dialog } from "@mui/material";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import Button from "./Button";
import NewsletterGraphic from "../images/illustrations/Email-campaign-bro.svg";

const DEFAULT_LOCAL_STORAGE_KEY = "xper_newsletter_dismissed_v1";

async function subscribe(email) {
  const res = await fetch(
    "/api/subscribe",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    }
  );

  const text = await res.text();
  let data = null;
  if (text) {
    try { data = JSON.parse(text); } catch (e) { /* non-JSON error page or empty body */ }
  }
  if (!res.ok) {
    const message = (data && (data.error || data.message)) || `Request failed (${res.status})`;
    throw new Error(message);
  }
  return data ?? { success: true };
}

const NewsletterPopup = () => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [email, setEmail] = useState("");
  const [pending, setPending] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const emailRef = useRef(null);
  const successRef = useRef(null);
  useEffect(() => {
    try {
      if (localStorage.getItem(DEFAULT_LOCAL_STORAGE_KEY) === "true") return;
    } catch { /* The popup must still work when storage is unavailable. */ }
    const timer = setTimeout(() => setIsVisible(true), 30000);
    return () => clearTimeout(timer);
  }, []);

  const dismiss = () => {
    setIsVisible(false);
    try { localStorage.setItem(DEFAULT_LOCAL_STORAGE_KEY, "true"); } catch { /* optional persistence */ }
  };
  const onSubmit = async (event) => {
    event.preventDefault();
    if (pending) return;
    if (!emailRef.current.validity.valid || !email.trim()) {
      setError(t("newsletter.invalidEmail"));
      emailRef.current.focus();
      return;
    }
    setError("");
    setPending(true);
    try {
      await subscribe(email.trim());
      setSubmitted(true);
      try { localStorage.setItem(DEFAULT_LOCAL_STORAGE_KEY, "true"); } catch { /* optional persistence */ }
    } catch {
      setError(t("newsletter.error"));
    } finally {
      setPending(false);
    }
  };
  useEffect(() => { if (submitted) successRef.current?.focus(); }, [submitted]);

  return (
    <Dialog open={isVisible} onClose={dismiss} aria-labelledby="newsletter-title"
      aria-describedby="newsletter-desc" maxWidth="sm" fullWidth
      PaperProps={{ className: "rounded-2xl bg-white", style: { borderRadius: 16 } }}>
      <button type="button" autoFocus aria-label={t("newsletter.close")} onClick={dismiss}
        className="absolute right-3 top-3 z-10 rounded-full p-2 text-main focus-visible:outline focus-visible:outline-2 focus-visible:outline-main">
        <X aria-hidden="true" size={24} />
      </button>
      <div className="grid grid-cols-1 md:grid-cols-5">
        <div className="relative hidden md:block md:col-span-2 bg-main">
          <img src={NewsletterGraphic} alt="" className="absolute inset-0 h-full w-full object-contain p-6" />
        </div>
        <div className="md:col-span-3 p-6 md:p-12 text-main">
          <h2 id="newsletter-title" className="mt-3 text-3xl font-bold leading-snug">{t("newsletter.title")}</h2>
          <p id="newsletter-desc" className="mt-3">{t("newsletter.description")}</p>
          {submitted ? (
            <p ref={successRef} tabIndex={-1}>{t("newsletter.success")}</p>
          ) : (
            <form noValidate onSubmit={onSubmit} aria-busy={pending}>
              <label htmlFor="newsletter-email" className="block mb-2">{t("newsletter.email")}</label>
              <input id="newsletter-email" ref={emailRef} type="email" name="email" autoComplete="email" required
                readOnly={pending} aria-invalid={Boolean(error)} aria-describedby={error ? "newsletter-error" : undefined}
                className="w-full p-2 border border-main rounded-md text-main focus-visible:outline focus-visible:outline-2 focus-visible:outline-main"
                value={email} onChange={(event) => { setEmail(event.target.value); setError(""); }} />
              {error && <p id="newsletter-error" role="alert" className="mt-2 text-red-700">{error}</p>}
              <Button type="submit" size="large" className="w-full mt-4" disabled={pending}>{t("newsletter.title")}</Button>
            </form>
          )}
          <p role="status" aria-atomic="true" className="mt-2 mb-0">{pending ? t("newsletter.sending") : ""}</p>
        </div>
      </div>
    </Dialog>
  );
};

export default NewsletterPopup;
